import { QSetStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { QSetDeletionModal } from '@exam-management/qset-deletion';
import { QSetDuplicationModal } from '@exam-management/qset-duplication';
import { QSetExportFormModal } from '@exam-management/qset-export';
import {
  ContentCopyRoundedIcon,
  DeleteOutlineIcon,
  FileDownloadOutlinedIcon,
  MoreMenuIconButton,
  type MoreMenuIconButtonOption,
} from '@shared/components';
import { usePermission } from '@shared/permissions';
import { useCallback, useMemo, useState } from 'react';

import { QSetListModalAction } from '../../providers';

interface MoreActionMenuProps {
  qSetId: string;
  status: QSetStatus;
}

interface MoreActionMenuOption extends MoreMenuIconButtonOption {
  status: QSetStatus[];
  requiredPermission: string[];
}

export default function MoreActionMenu(props: Readonly<MoreActionMenuProps>) {
  const { qSetId, status } = props;
  const { permissions } = usePermission();
  const [action, setAction] = useState<QSetListModalAction | null>(null);

  const handleClose = useCallback(() => {
    setAction(null);
  }, []);

  const handleDuplicate = useCallback(() => {
    setAction(QSetListModalAction.QSET_DUPLICATE);
  }, []);

  const handleExport = useCallback(() => {
    setAction(QSetListModalAction.QSET_EXPORT);
  }, []);

  const handleDelete = useCallback(() => {
    setAction(QSetListModalAction.QSET_DELETE);
  }, []);

  const options: MoreActionMenuOption[] = useMemo(
    () => [
      {
        label: 'Duplicate',
        icon: <ContentCopyRoundedIcon />,
        onClick: handleDuplicate,
        requiredPermission: [APP_PERMISSIONS.EXAM.QSET.CREATE, APP_PERMISSIONS.EXAM.QCONFIG.VIEW],
        status: [
          QSetStatus.Draft,
          QSetStatus.Published,
          QSetStatus.Unpublished,
          QSetStatus.Reviewed,
          QSetStatus.Disabled,
        ],
      },
      {
        label: 'Export QSet',
        icon: <FileDownloadOutlinedIcon />,
        onClick: handleExport,
        requiredPermission: [],
        status: [
          QSetStatus.Draft,
          QSetStatus.Published,
          QSetStatus.Unpublished,
          QSetStatus.InReview,
          QSetStatus.Reviewed,
          QSetStatus.Disabled,
        ],
      },
      {
        label: 'Delete',
        icon: (
          <DeleteOutlineIcon
            sx={(theme) => ({
              color: theme.palette.error.main,
            })}
          />
        ),
        onClick: handleDelete,
        requiredPermission: [APP_PERMISSIONS.EXAM.QSET.DELETE],
        status: [QSetStatus.Draft],
        sx: (theme) => ({
          color: theme.palette.error.main,
        }),
      },
    ],
    [handleDuplicate, handleExport, handleDelete],
  );

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          option.status.includes(status) &&
          option.requiredPermission.every((reqPermission) => permissions.includes(reqPermission)),
      ),
    [options, status, permissions],
  );

  return (
    <>
      <MoreMenuIconButton options={filteredOptions} />
      <QSetDuplicationModal
        qSetId={qSetId}
        open={action === QSetListModalAction.QSET_DUPLICATE}
        onClose={handleClose}
      />
      <QSetExportFormModal
        qSetId={qSetId}
        open={action === QSetListModalAction.QSET_EXPORT}
        onClose={handleClose}
      />
      <QSetDeletionModal
        qSetId={qSetId}
        open={action === QSetListModalAction.QSET_DELETE}
        onClose={handleClose}
      />
    </>
  );
}
